import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api } from './api';

/** Centralised query keys so mutations can invalidate precisely. */
export const keys = {
  dashboard: ['dashboard'] as const,
  groups: ['groups'] as const,
  group: (id: string) => ['group', id] as const,
  expenses: (id: string) => ['group', id, 'expenses'] as const,
  expense: (groupId: string, expenseId: string) => ['group', groupId, 'expense', expenseId] as const,
  balances: (id: string) => ['group', id, 'balances'] as const,
  payments: (id: string) => ['group', id, 'payments'] as const,
  requests: (id: string) => ['group', id, 'requests'] as const,
  stats: (id: string) => ['group', id, 'stats'] as const,
  recurring: (id: string) => ['group', id, 'recurring'] as const,
  invite: (code: string) => ['invite', code] as const,
};

export const useDashboard = () => useQuery({ queryKey: keys.dashboard, queryFn: api.dashboard });

export const useGroups = () =>
  useQuery({ queryKey: keys.groups, queryFn: async () => (await api.groups()).groups });

export const useGroup = (groupId: string) =>
  useQuery({ queryKey: keys.group(groupId), queryFn: () => api.group(groupId) });

export const useExpenses = (groupId: string) =>
  useInfiniteQuery({
    queryKey: keys.expenses(groupId),
    queryFn: ({ pageParam }) => api.expenses(groupId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

export const useExpense = (groupId: string, expenseId: string | undefined) =>
  useQuery({
    queryKey: keys.expense(groupId, expenseId ?? ''),
    queryFn: async () => (await api.expense(groupId, expenseId!)).expense,
    enabled: Boolean(expenseId),
  });

export const useBalances = (groupId: string) =>
  useQuery({ queryKey: keys.balances(groupId), queryFn: () => api.balances(groupId) });

/** Plan and scan usage; refreshed after scans and upgrades. */
export const usePlan = () => useQuery({ queryKey: ['plan'], queryFn: api.plan });

export const useStats = (groupId: string) => useQuery({ queryKey: keys.stats(groupId), queryFn: () => api.stats(groupId) });

export const usePaymentRequests = (groupId: string) =>
  useQuery({ queryKey: keys.requests(groupId), queryFn: async () => (await api.paymentRequests(groupId)).requests });

export const useRecurring = (groupId: string) =>
  useQuery({ queryKey: keys.recurring(groupId), queryFn: async () => (await api.recurring(groupId)).recurring });

export const usePayments = (groupId: string) =>
  useQuery({ queryKey: keys.payments(groupId), queryFn: async () => (await api.payments(groupId)).payments });

/**
 * After any change to a group's money, every derived view (group summary,
 * balances, dashboard, group list) is refreshed from the server so the
 * numbers shown always come from the authoritative calculation.
 */
export function useInvalidateGroup() {
  const queryClient = useQueryClient();
  return useCallback(
    async (groupId?: string) => {
      await Promise.all([
        groupId ? queryClient.invalidateQueries({ queryKey: ['group', groupId] }) : undefined,
        queryClient.invalidateQueries({ queryKey: keys.groups }),
        queryClient.invalidateQueries({ queryKey: keys.dashboard }),
      ]);
    },
    [queryClient],
  );
}
